import type { QueryClient } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import {
  createRootRouteWithContext,
  type ErrorComponentProps,
  Outlet,
  ScrollRestoration,
} from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/router-devtools'
import { Meta, Scripts } from '@tanstack/start'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthProvider } from '@/domain/auth'
import { restoreLanguage } from '@/i18n'
import { initPostHog } from '@/lib/posthog'
import { captureException, initSentry } from '@/lib/sentry'
import { DevToolsWidget } from '@/ui/components/dev-tools/dev-tools-widget'
import { ErrorState } from '@/ui/components/state-error'

import appCss from '../../src/styles/globals.css?url'

interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  errorComponent: RootErrorComponent,
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { title: 'Brand App' },
      { name: 'description', content: 'A production-ready React starter with TanStack' },
    ],
    links: [
      // Preload the latin subsets of both families (the ones every page uses) to
      // cut first-paint FOUT. Fonts fetch in CORS mode, so `crossOrigin` is required
      // even same-origin — without it the preload wouldn't be reused by @font-face.
      {
        rel: 'preload',
        href: '/fonts/dm-sans-latin.woff2',
        as: 'font',
        type: 'font/woff2',
        crossOrigin: 'anonymous',
      },
      {
        rel: 'preload',
        href: '/fonts/bricolage-grotesque-latin.woff2',
        as: 'font',
        type: 'font/woff2',
        crossOrigin: 'anonymous',
      },
      { rel: 'stylesheet', href: appCss },
    ],
  }),
})

function RootComponent() {
  useEffect(() => {
    void initSentry()
    void initPostHog()
  }, [])

  return (
    <RootDocument>
      <AuthProvider>
        <Outlet />
        <DevToolsWidget />
      </AuthProvider>
    </RootDocument>
  )
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  useEffect(() => {
    captureException(error)
  }, [error])

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <ErrorState onRetry={reset} />
    </div>
  )
}

function RootDocument({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation()

  // Restore the persisted language only after hydration so the initial render
  // matches the server (which always starts from the deterministic fallback).
  useEffect(() => {
    restoreLanguage()
  }, [])

  return (
    <html lang={i18n.language}>
      <head>
        {/* Must run before any module scripts - sets React refresh preamble */}
        {import.meta.env.DEV && (
          <script
            dangerouslySetInnerHTML={{
              __html: `
                window.__vite_plugin_react_preamble_installed__ = true;
                window.$RefreshReg$ = () => {};
                window.$RefreshSig$ = () => (type) => type;
              `,
            }}
          />
        )}
        <Meta />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
        {import.meta.env.DEV && (
          <>
            <TanStackRouterDevtools position="bottom-right" />
            <ReactQueryDevtools buttonPosition="bottom-left" />
          </>
        )}
      </body>
    </html>
  )
}
